// What to show, by who is signed in (E12-S1-T2). Two guards, both about display only: the API
// decides what is allowed, and a page that asks for something the principal may not have gets a 403
// from it whatever is drawn here.
//
//   SessionGuard  draws the hub's routes only for a signed-in principal. Signed out, every route
//                 shows the sign-in page and no page mounts, so the session check is the only
//                 request the app makes.
//   RequireRole   draws its children only for a principal holding one of the roles.
import type { Principal, Role } from "@waypoint/contracts";
import type { ReactNode } from "react";
import { Redirect, useRoute } from "wouter";
import { isApiError } from "../api/errors.js";
import { t } from "../i18n/t.js";
import { EmptyState, Page } from "../pages/page.js";
import { Frame } from "../shell/frame.js";
import { DEFAULT_RETURN_PATH, SIGNED_OUT_PATH } from "./return-path.js";
import { useSession } from "./session.js";
import { SignedOutPage } from "./sign-in.js";

export function SessionGuard({ children }: { children: (principal: Principal) => ReactNode }) {
  const state = useSession();
  const [onSignedOutPage] = useRoute(SIGNED_OUT_PATH);
  switch (state.status) {
    case "checking":
      return (
        <Frame>
          <p role="status" aria-busy className="text-wp-text-3">
            {t("session.checking")}
          </p>
        </Frame>
      );
    case "signed-out":
      return (
        <Frame>
          <SignedOutPage returnTo={state.returnTo} ended={onSignedOutPage} />
        </Frame>
      );
    case "failed": {
      const invalid = isApiError(state.error) && state.error.kind === "validation";
      return (
        <Frame>
          <Page title={t(invalid ? "session.invalid.title" : "session.failed.title")}>
            <EmptyState
              icon="alert"
              title={t(invalid ? "session.invalid.line" : "session.failed.line")}
            />
          </Page>
        </Frame>
      );
    }
    case "signed-in":
      // Signed in after all (another tab signed in again): the signed-out page has nothing to say.
      if (onSignedOutPage) return <Redirect to={DEFAULT_RETURN_PATH} replace />;
      return <Frame principal={state.principal}>{children(state.principal)}</Frame>;
  }
}

export function RequireRole({
  principal,
  anyOf,
  fallback,
  children,
}: {
  principal: Principal;
  anyOf: readonly Role[];
  fallback: ReactNode;
  children: ReactNode;
}) {
  return anyOf.some((role) => principal.roles.includes(role)) ? children : fallback;
}
